import {MeasurementType} from "../measurement-models";
import {Doctor} from "./doctor";
import {emptyPatientData, PatientData} from "../upload/upload-model";
import {LocalizationStrings} from "../../i18n/i18n-model";
import {UserRole} from "../authentication/authenticated-user";
import {EcgWorkflow, ExternalAnalysisType} from "../hospitals/edit-hospital/hospital.details";
import {AssignType} from "./list-item";

export interface FoundMeasurement {
  readonly id: number;
  readonly code: string | undefined;
  readonly uniqueId: string | undefined;
  readonly uploadTime: string | undefined;
  readonly type: MeasurementType;
  readonly status: MeasurementStatus;
  readonly hospitalName: string;
  readonly patientData: PatientData | undefined;
  readonly bmi: number | undefined;
  readonly essScore: number | undefined;
  readonly doctor: Doctor | undefined;
  readonly deliveryTime: string | undefined;
  readonly durationDays: number | undefined;
  readonly nurse: Nurse;
  readonly startTime: string | undefined;
  readonly nurseDownloadedReport: boolean;
  readonly reportExists?: boolean;
  readonly deviceSerialNumber?: string;
  readonly initiateTime: string | undefined;

  readonly hasOperatorHospital: boolean;

  readonly asyncJobStatus?: AsyncJobStatus;

  readonly deleted: boolean;
  readonly commentCount?: number;

  readonly technician?: Doctor;

  readonly reportLanguage?: string;

  readonly fileName?: string;
  readonly dataSize?: number;

  readonly chain: Chain | undefined;
  /** @deprecated Use `homeStatus` instead, should be undefined for non-home measurements */
  readonly isHome: boolean;
  readonly specifiedHomeHolterDuration: number | undefined;

  readonly mailingInfoSetAt: string | undefined;
  readonly deviceMailedAt: string | undefined;
  readonly homeStatus: HomeMeasurementStatus | undefined;
  readonly measuringCompletedAt: string | undefined;
  readonly eFormLanguage?: string;
  readonly eFormCode?: string;
  readonly isOrderedByCurrentHospital: boolean;
  readonly assignSleepTechniciansEnabled: boolean | undefined;
  readonly ecgWorkflow: EcgWorkflow | undefined;
  readonly assignType: AssignType | undefined;
  readonly technicianAssignType: AssignType | undefined;

  readonly currentlyAnalysed: boolean | undefined;
  readonly usesDateOfBirth: boolean;
  readonly isCasHospital: boolean;
  readonly externalAnalysisType?: ExternalAnalysisType;
}

export interface Nurse {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email: string;
}

export interface Chain {
  readonly id: number;
  readonly name: string;
}

export const ALL_MEASUREMENT_STATUSES = [
  "UPLOADED",
  "READY_FOR_ANALYSIS",
  "READY_FOR_POST_ANALYSIS",
  "DONE",
  "REJECTED_BY_DATA_OFFICER",
  "PRE_ANALYSED",
  "INITIATED",
  "PRE_ANALYSIS"
] as const;
type MeasurementStatusTuple = typeof ALL_MEASUREMENT_STATUSES;
export type MeasurementStatus = MeasurementStatusTuple[number]; // union type

export function allStatusesExcept(...statuses: MeasurementStatus[]): MeasurementStatus[] {
  return ALL_MEASUREMENT_STATUSES.filter((status) => !statuses.includes(status));
}

export const ALL_HOME_MEASUREMENT_STATUSES = [
  "WAITING_FOR_MAILING_INFO",
  "MAILING_TO_PATIENT",
  "MAILED_TO_PATIENT",
  "RETURNED_FROM_PATIENT"
] as const;
type HomeMeasurementStatusTuple = typeof ALL_HOME_MEASUREMENT_STATUSES;
export type HomeMeasurementStatus = HomeMeasurementStatusTuple[number]; // union type

export const measurementStatusLocalizationKeys: ReadonlyMap<MeasurementStatus, ReadonlyMap<UserRole, keyof LocalizationStrings>> = new Map<
  MeasurementStatus,
  ReadonlyMap<UserRole, keyof LocalizationStrings>
>()
  .set(
    "UPLOADED",
    new Map<UserRole, keyof LocalizationStrings>()
      .set("DATA_OFFICER", "measurementStatusUploaded")
      .set("NURSE", "nurseMeasurementStatusProcessing")
  )
  .set(
    "READY_FOR_ANALYSIS",
    new Map<UserRole, keyof LocalizationStrings>()
      .set("DATA_OFFICER", "measurementStatusReadyForAnalysis")
      .set("NURSE", "nurseMeasurementStatusProcessing")
      .set("DOCTOR", "measurementStatusReadyForAnalysis")
  )
  .set(
    "READY_FOR_POST_ANALYSIS",
    new Map<UserRole, keyof LocalizationStrings>()
      .set("DATA_OFFICER", "measurementStatusReadyForPostAnalysis")
      .set("NURSE", "nurseMeasurementStatusProcessing")
      .set("DOCTOR", "measurementStatusReadyForPostAnalysis")
  )
  .set(
    "DONE",
    new Map<UserRole, keyof LocalizationStrings>()
      .set("DATA_OFFICER", "measurementStatusDone")
      .set("NURSE", "nurseMeasurementStatusDone")
      .set("DOCTOR", "measurementStatusDone")
  )
  .set(
    "REJECTED_BY_DATA_OFFICER",
    new Map<UserRole, keyof LocalizationStrings>()
      .set("DATA_OFFICER", "measurementStatusRejectedByDataOfficer")
      .set("NURSE", "nurseMeasurementStatusRejected")
  )
  .set(
    "PRE_ANALYSED",
    new Map<UserRole, keyof LocalizationStrings>()
      .set("DATA_OFFICER", "measurementStatusPreAnalysed")
      .set("NURSE", "nurseMeasurementStatusProcessing")
      .set("DOCTOR", "measurementStatusPreAnalysed")
  )
  .set(
    "INITIATED",
    new Map<UserRole, keyof LocalizationStrings>()
      .set("DATA_OFFICER", "measurementStatusInitiated")
      .set("NURSE", "measurementStatusInitiated")
  )
  .set("PRE_ANALYSIS",
    new Map<UserRole, keyof LocalizationStrings>()
      .set("DATA_OFFICER", "measurementStatusPreAnalysis")
      .set("DOCTOR", "measurementStatusPreAnalysis")
      .set("NURSE", "nurseMeasurementStatusProcessing")
  );

export type AsyncJobStatus = "PENDING" | "FAILED" | "SUCCESS";

export function emptyFoundMeasurement(): FoundMeasurement {
  return {
    id: 123456,
    uniqueId: "",
    patientData: emptyPatientData(),
    hospitalName: "",
    code: "",
    uploadTime: undefined,
    type: "SLEEP",
    status: "INITIATED",
    bmi: undefined,
    essScore: undefined,
    doctor: undefined,
    deliveryTime: undefined,
    durationDays: undefined,
    nurse: {email: "", firstName: undefined, lastName: undefined},
    startTime: undefined,
    nurseDownloadedReport: false,
    initiateTime: undefined,
    hasOperatorHospital: false,
    deleted: false,
    asyncJobStatus: undefined,
    commentCount: undefined,
    dataSize: undefined,
    deviceSerialNumber: undefined,
    fileName: undefined,
    reportLanguage: undefined,
    technician: undefined,
    chain: undefined,
    isHome: false,
    specifiedHomeHolterDuration: undefined,
    mailingInfoSetAt: undefined,
    homeStatus: undefined,
    deviceMailedAt: undefined,
    measuringCompletedAt: undefined,
    isOrderedByCurrentHospital: false,
    assignSleepTechniciansEnabled: undefined,
    ecgWorkflow: undefined,
    assignType: undefined,
    technicianAssignType: undefined,
    currentlyAnalysed: undefined,
    usesDateOfBirth: false,
    isCasHospital: false,
    externalAnalysisType: "CAS",
  };
}

export function formatMeasurementFileSize(size: number | undefined): string {
  if (size === undefined) {
    return "";
  }
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}
